import { z } from 'zod';
import { ErpSettingsSchema } from './erp.schema.js';
import { SecuritySettingsSchema } from './security.schema.js';
import type { FeatureFlags } from '../features.js';
import type { TerminologySettingsInput } from '../terminology.js';

/**
 * Feature flags and terminology are plain data and live in zod-free modules so
 * the web console's boot path can read them without pulling zod and this whole
 * schema into the entry chunk. They are re-exported here so every existing
 * `from '@attendance/shared/schemas/settings'` import keeps working unchanged.
 */
export {
  FEATURE_KEYS,
  FEATURE_DEFAULTS,
  LEAN_ATTENDANCE_FEATURES,
  OFFICE_ONLY_FEATURES,
  TERMINAL_ONLY_FEATURES,
  type FeatureKey,
  type FeatureFlags,
} from '../features.js';
export {
  TERMINOLOGY_DEFAULTS,
  resolveTerminology,
  type TerminologyProfile,
  type TerminologyTermKey,
  type TerminologyTerms,
  type TerminologySettingsInput,
} from '../terminology.js';

export const GeneralSettingsSchema = z.object({
  timezone: z.string().default('Asia/Dubai'),
  weekStart: z.number().min(0).max(6).default(1),
  weekendDays: z.array(z.number().min(0).max(6)).default([5, 6]),
  dateFormat: z.enum(['DD-MM-YYYY', 'MM-DD-YYYY', 'YYYY-MM-DD']).default('DD-MM-YYYY'),
  timeFormat: z.enum(['12h', '24h']).default('24h'),
});

export const AttendanceSettingsSchema = z.object({
  sources: z
    .object({
      web: z.boolean().default(true),
      kiosk: z.boolean().default(true),
      mobile: z.boolean().default(false),
    })
    // Without this default, parsing a Company.settings that doesn't have a
    // pre-populated `attendance.sources` object (e.g. an install that
    // skipped seeding the full settings JSON) hard-fails the dashboard
    // route. Every leaf already has a default; surfacing one here makes
    // the parent optional too.
    .default(() => ({ web: true, kiosk: true, mobile: false })),
  // Per-source photo requirements. When the source's flag is true the
  // service rejects punches that lack a photo.
  //
  // `exemptRemote` waives the web selfie requirement AND the web face-match
  // (faceRecognition.verifyOnWeb) for punches that skip the geofence — a worker
  // whose attendance location is ANYWHERE or a worksite flagged "allow remote
  // punch". Lets an admin keep selfies + face-proof for on-site web punches
  // while letting trusted "punch from anywhere" staff clock in with no face
  // proof. Defaults false, so existing installs keep requiring the selfie.
  requirePhoto: z
    .object({
      web: z.boolean().default(false),
      kiosk: z.boolean().default(false),
      mobile: z.boolean().default(true),
      exemptRemote: z.boolean().default(false),
    })
    .default(() => ({ web: false, kiosk: false, mobile: true, exemptRemote: false })),
  gpsAccuracyThresholdMeters: z.number().int().min(10).max(500).default(50),
  /**
   * How many trailing CLOSED days the nightly day-close job re-checks
   * (`markAbsences.job.ts`).
   *
   * The job writes an AttendanceDay for every expected worker who has no row on
   * a day that has ended — ABSENT, or WEEKEND / HOLIDAY / ON_LEAVE where that is
   * the honest verdict. It re-sweeps a WINDOW rather than only yesterday so a
   * night the server was down (or the job sat FAILED past `maxAttempts`) heals
   * on the next run instead of leaving a permanent blank in the calendar. The
   * sweep is idempotent — an existing row is never touched — so the only cost of
   * a longer window is a slightly wider range read.
   *
   * Raise it if this install has long outages; there is no reason to lower it
   * below the longest gap you are willing to leave unhealed. It is NOT the knob
   * for loading history: closing a tenant's first months is a one-shot job for
   * `scripts/backfill-absences.ts`, not unbounded nightly work.
   */
  absenceSweepDays: z.number().int().min(1).max(90).default(14),
  /**
   * The OPERATING REGION — the outer bound on where attendance may be recorded
   * at all, independent of any worksite geofence.
   *
   * `attendanceLocation: ANYWHERE` meant anywhere on Earth. A punch reporting
   * coordinates on another continent was flagged for missing a geofence and
   * still counted toward hours and pay, with that flag the only trace. This is
   * the bound "anywhere" was always assumed to have.
   *
   * A punch outside it is ACCEPTED AND FLAGGED, never rejected (client
   * decision, 2026-08-10). Rejecting would strand a genuinely travelling admin
   * with no way to clock in, and the same reasoning already governs a missing
   * GPS fix: record the fact, mark it, let a human judge it.
   *
   * Defaults bound the UAE. Deliberately a setting rather than a constant — the
   * product may be sold elsewhere, and a hardcoded country is exactly the kind
   * of assumption nobody finds until it is wrong for a customer. Turn `enabled`
   * off to restore the old unbounded behaviour.
   */
  allowedRegion: z
    .object({
      enabled: z.boolean().default(true),
      // UAE, generous by a few tenths so a coastal or border reading with poor
      // accuracy is not flagged for being at the edge of the country.
      minLat: z.number().min(-90).max(90).default(22.5),
      maxLat: z.number().min(-90).max(90).default(26.2),
      minLng: z.number().min(-180).max(180).default(51.4),
      maxLng: z.number().min(-180).max(180).default(56.5),
    })
    .default(() => ({
      enabled: true,
      minLat: 22.5,
      maxLat: 26.2,
      minLng: 51.4,
      maxLng: 56.5,
    })),
  // Soft-flag mode for mock-location: 'reject' (current) hard-rejects with
  // 422; 'flag' persists the event with status=FLAGGED for forensic review;
  // 'allow' silently records (useful only for QA / debug).
  mockLocationMode: z.enum(['reject', 'flag', 'allow']).default('reject'),
  // The "ways of access" this company OFFERS — a checklist. Each worker is then
  // assigned ONE of the offered methods on their profile
  // (Employee.attendanceMode), and THAT governs their enforcement — the
  // per-worker mode is authoritative in both directions.
  //
  // Unchecking a method only removes it from the picker for NEW assignments;
  // already-assigned workers keep their method until reassigned (never silently
  // re-gate a live workforce).
  //
  // This replaced the single-select `punchPolicy` (worksite_only / gps_anywhere
  // / kiosk_only), whose three values were really just company-wide versions of
  // three of these modes — and which could override a strict per-worker setting.
  // To disable web self-punch company-wide, turn off the `sources.web` toggle.
  accessModes: z
    .object({
      assignedWorksite: z.boolean().default(true),
      geofence: z.boolean().default(true),
      kiosk: z.boolean().default(true),
      app: z.boolean().default(true),
    })
    .default({ assignedWorksite: true, geofence: true, kiosk: true, app: true }),
  minPunchIntervalSeconds: z.number().int().min(0).max(600).default(30),
  serverClientTimeDeltaMaxSeconds: z.number().int().min(30).max(900).default(120),
  autoPunchOut: z
    .object({
      enabled: z.boolean().default(true),
      atTime: z.string().default('23:59'),
      action: z.enum(['mark_complete', 'flag', 'none']).default('flag'),
    })
    // Same pre-existing-install defence as `sources` above — without this
    // default an empty Company.settings.attendance object 422s the
    // dashboard route.
    .default(() => ({ enabled: true, atTime: '23:59', action: 'flag' as const })),
  /**
   * "You are still checked in" — a nudge to the WORKER while the day can still
   * be closed honestly, hours before `autoPunchOut` runs and repairs the record
   * with a synthetic stamp nobody can vouch for.
   *
   * `afterMinutes` defaults to 600 (10h): a standard day is 8h of work plus a
   * 1h break, so ten hours open is past any normal shift without firing on
   * someone who simply worked late.
   */
  missedCheckoutReminder: z
    .object({
      enabled: z.boolean().default(true),
      afterMinutes: z.number().int().min(60).max(1440).default(600),
    })
    // Same pre-existing-install defence as `autoPunchOut` above: without the
    // default, an attendance blob written before this field 422s every route
    // that parses settings.
    .default(() => ({ enabled: true, afterMinutes: 600 })),
  graceDefaultMinutes: z.number().int().min(0).max(120).default(15),
  halfDayThresholdHours: z.number().min(0).max(24).default(4),
  fullDayThresholdHours: z.number().min(0).max(24).default(8),
  allowManualEntry: z.boolean().default(true),
  // Biometric-data governance (PDPL): how long punch audit selfies (kiosk +
  // web) are retained on disk before a daily purge job deletes them. 0 means
  // keep forever. Defaulting here means existing Company.settings JSON that
  // predates this field still parses — the field materialises as 90.
  auditPhotoRetentionDays: z.number().int().min(0).max(3650).default(90),
});

// Per `04-settings-schema.md` §FaceRecognition — admin-tunable knobs for the
// kiosk face flow. enabled=false hides the "Use face" button and refuses
// face-punch endpoint. requiredOnKiosk=true hides "Use PIN" (face-only).
/** WHO the web face match reaches — see `webScope` below. */
export const FaceWebScopeSchema = z.enum([
  'everyone',
  'site_workers',
  'office_staff',
]);
export type FaceWebScope = z.infer<typeof FaceWebScopeSchema>;

export const FaceRecognitionSettingsSchema = z.object({
  enabled: z.boolean().default(false),
  requiredOnKiosk: z.boolean().default(false),
  // Distance threshold for face-api.js descriptor match. Lower = stricter.
  // 0.6 is face-api.js's recommended default for medium-quality recognition.
  minConfidence: z.number().min(0).max(1).default(0.6),
  // Frontend bails the camera loop after this many failed detections.
  maxAttempts: z.number().int().min(1).max(20).default(3),
  fallbackToPin: z.boolean().default(true),
  storeAuditPhoto: z.boolean().default(true),
  /**
   * May a worker's most recent PUNCH photo stand in as their avatar when they
   * have never uploaded a profile picture?
   *
   * OFF BY DEFAULT, and that default is the decision, not an oversight.
   *
   * The photos this reaches were collected as attendance evidence: they are
   * written by the kiosk / web / field punch paths, `field-photos` is encrypted
   * at rest, and every one of them is deleted by the audit-photo retention job
   * on `attendance.auditPhotoRetentionDays`. Showing one as an avatar is a NEW
   * PURPOSE for data gathered under a narrower one, so it has to be a decision
   * an administrator takes knowingly. An install that upgrades into this file
   * must not start displaying its workforce's faces because a default flipped.
   *
   * Turning it on does NOT widen who may see a face. The serve path applies the
   * same self / ADMIN / HR / direct-manager gate that already guards
   * `GET /attendance/events/:id/photo`; everyone else gets exactly what they get
   * today, which is initials. It also does not copy anything: the avatar is the
   * retained evidence file itself, so when retention purges it the avatar
   * reverts to initials, by design.
   */
  useCaptureAsAvatar: z.boolean().default(false),
  /**
   * How long the enrollment capture photo behind a worker's FieldFaceTemplate
   * set (FieldEnrollmentImage) is kept before the retention job deletes it —
   * server changes for field APK, item 2. PDPL requires this be FINITE and
   * configurable, unlike `attendance.auditPhotoRetentionDays` (which allows 0
   * = forever): an enrollment photo is a standing biometric record kept
   * specifically so a future model swap can re-embed without a physical
   * re-capture, not evidence of one event, so "keep forever" is not an
   * available answer here.
   */
  faceEnrollmentImageRetentionMonths: z.number().int().min(1).max(120).default(24),
  // How a FIELD face punch is trusted (field-security redesign P3):
  //   'auto_count' — a device-asserted clean FACE match counts immediately
  //                  (today's behaviour; the device is trusted).
  //   'verify'     — a FACE punch lands PROVISIONAL (does not count toward the
  //                  rollup/payroll) until the server RE-MATCHES the uploaded
  //                  descriptor against its own authoritative gallery. A genuine
  //                  match auto-promotes; a forged/missing descriptor stays
  //                  PENDING_APPROVAL for HR. Closes the "device asserts its own
  //                  match score" forgery hole.
  fieldReviewMode: z.enum(['auto_count', 'verify']).default('auto_count'),
  // Similarity margin the claimed worker must beat the next-closest worker by
  // for the server re-match to auto-promote a 'verify'-mode FACE punch.
  fieldReverifyMargin: z.number().min(0).max(1).default(0.04),
  // When on, a WEB self check-in must include a face embedding that matches the
  // employee's enrolled field-face gallery (1:1) — otherwise the selfie is an
  // audit photo only and any image is accepted. Off by default so existing
  // deployments keep today's behaviour until faces are enrolled.
  verifyOnWeb: z.boolean().default(false),
  /**
   * WHO has to prove their face on a web check-in — BOTH the selfie
   * (`attendance.requirePhoto.web`) and the 1:1 match (`verifyOnWeb`).
   *
   * The requirement used to be all-or-nothing across the company, with a single
   * carve-out for people whose attendance location is ANYWHERE
   * (`attendance.requirePhoto.exemptRemote`). That is one axis — WHERE somebody
   * checks in from — and it cannot express the split most companies actually
   * want, which is about WHO: enforce the face for the site workforce, whose
   * punches are the ones worth forging, and leave the office staff checking in
   * and out at a desk to do it with a tap.
   *
   * It governs BOTH halves deliberately, because they are one act to the person
   * doing it: the camera opens if EITHER is required, so a scope that narrowed
   * only the match still put a viewfinder in front of an office worker who was
   * then told a photo was mandatory. The precedent is already in the codebase —
   * `requirePhoto.exemptRemote` waives the selfie and the match together, for
   * exactly the same reason.
   *
   * So the switches say WHETHER, and this says WHOM, keyed on the same
   * `Employee.workforceType` column the rest of the app splits the two
   * populations by:
   *
   *   'everyone'      — both populations (the previous behaviour, so an existing
   *                     install upgrades to exactly what it had).
   *   'site_workers'  — SITE_WORKER only; office staff check in with a tap, no
   *                     camera opened and no photo demanded.
   *   'office_staff'  — OFFICE_STAFF only.
   *
   * The remote waiver still applies ON TOP of this: someone in scope here who
   * is also ANYWHERE-eligible is still waived when exemptRemote is on. The two
   * narrow independently and neither can widen the other.
   */
  webScope: FaceWebScopeSchema.default('everyone'),
});

/**
 * Does the web face requirement — selfie AND match — reach this worker?
 *
 * Shared by the punch path (which enforces it) and punchContext (which tells
 * the browser whether to open the camera), so the screen can never prompt for a
 * face the server will not ask for, or stay silent about one it will.
 */
export function faceWebScopeApplies(
  scope: FaceWebScope | null | undefined,
  workforceType: 'SITE_WORKER' | 'OFFICE_STAFF' | null | undefined
): boolean {
  // Both unknowns resolve the SAME way — toward asking for the face — because
  // this gate decides whether to demand proof of identity, and every way of not
  // knowing ("settings written before this field existed", "employee has no
  // workforce type") must not silently become "no proof needed".
  if (scope !== 'site_workers' && scope !== 'office_staff') return true;
  if (!workforceType) return true;
  return scope === 'site_workers'
    ? workforceType === 'SITE_WORKER'
    : workforceType === 'OFFICE_STAFF';
}

export const OvertimeSettingsSchema = z.object({
  enabled: z.boolean().default(true),
  thresholdMinutesPerDay: z.number().int().min(0).default(0),
  maxDailyMinutes: z.number().int().min(0).max(720).default(240),
  weekdayMultiplier: z.number().min(1).max(5).default(1.25),
  weekendMultiplier: z.number().min(1).max(5).default(1.5),
  holidayMultiplier: z.number().min(1).max(5).default(1.5),
});

export const LeaveSettingsSchema = z.object({
  fiscalYearAlignment: z.enum(['calendar', 'joining_date']).default('calendar'),
  allowNegativeBalance: z.boolean().default(false),
  minNoticeDaysDefault: z.number().int().min(0).max(365).default(2),
  weekendIncludedInLeave: z.boolean().default(false),
  holidaysIncludedInLeave: z.boolean().default(false),
  notifyManagerOnRequest: z.boolean().default(true),
  notifyHrOnRequest: z.boolean().default(false),
});

/**
 * Sign-in and security policy — its own module now (`security.schema.ts`),
 * re-exported here so every existing import keeps working.
 */
export { SecuritySettingsSchema };

export const BrandingSettingsSchema = z.object({
  primaryColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/).default('#6C47FF'),
  appName: z.string().min(1).max(60).default('Attendance'),
  logoUrl: z.string().optional().nullable(),
  emailFooter: z.string().optional().nullable(),
});

// ---------------------------------------------------------------------------
// HR letter templates (salary certificate / NOC / experience).
//
// Each letter body is an editable, free-text template. Paragraphs are
// separated by a blank line (`\n\n`); `letter.service.ts` renders one PDF
// paragraph per template paragraph. Placeholders use `{{token}}` syntax and
// are interpolated at render time. Available tokens (unknown tokens are left
// verbatim so a typo is visible rather than silently dropped):
//   {{employeeName}} {{companyName}} {{legalName}} {{designation}}
//   {{department}} {{joiningDate}} {{issueDate}} {{passport}} {{emiratesId}}
//   {{purpose}} {{currency}} {{basicSalary}} {{allowances}} {{totalSalary}}
//
// The English defaults below reproduce the exact wording the service used
// before templating, so an install that has never edited these renders an
// identical letter. Arabic variants are optional and empty by default; when a
// letter is requested in Arabic and the matching `*Ar` field is blank the
// service falls back to the English template. (pdfkit does not reshape/RTL
// Arabic glyphs — embedding the Arabic source text is in scope; correct RTL
// shaping is a known limitation, see letter.service.ts.)
// ---------------------------------------------------------------------------

const DEFAULT_SALARY_CERTIFICATE_EN =
  'This is to certify that {{employeeName}} (Passport No. {{passport}}; Emirates ID {{emiratesId}}) is employed with {{companyName}} as {{designation}} in the {{department}} department, since {{joiningDate}}.\n\n' +
  'The current monthly remuneration is as follows:\n\n' +
  'Basic salary:       {{currency}} {{basicSalary}}\n' +
  'Allowances:         {{currency}} {{allowances}}\n' +
  'Total monthly:      {{currency}} {{totalSalary}}\n\n' +
  'This certificate is issued upon the request of the employee for whatever legal purpose it may serve.';

const DEFAULT_NOC_EN =
  'This is to certify that {{employeeName}} (Passport No. {{passport}}; Emirates ID {{emiratesId}}) has been employed with {{companyName}} as {{designation}} since {{joiningDate}}.\n\n' +
  '{{companyName}} has no objection to the employee proceeding with {{purpose}}.\n\n' +
  'This NOC is issued upon the request of the employee and does not commit {{companyName}} to any liability or financial responsibility whatsoever.';

const DEFAULT_EXPERIENCE_EN =
  'This is to certify that {{employeeName}} has been employed with {{companyName}} as {{designation}} in the {{department}} department from {{joiningDate}} to date.\n\n' +
  'During this period, the employee discharged the assigned responsibilities with diligence and professionalism. We wish them success in their future endeavours.';

// Localised letter titles. English defaults match the previous KIND_TITLES.
export const LetterTitlesSchema = z.object({
  salaryCertificate: z.string().min(1).max(120).default('Salary Certificate'),
  noc: z.string().min(1).max(120).default('No Objection Certificate'),
  experience: z.string().min(1).max(120).default('Experience Letter'),
  salaryCertificateAr: z.string().max(120).default(''),
  nocAr: z.string().max(120).default(''),
  experienceAr: z.string().max(120).default(''),
});

export const LettersSettingsSchema = z.object({
  // Default fallback used by the NOC body when the requester supplies no
  // explicit purpose. Mirrors the old hardcoded fallback string.
  defaultNocPurpose: z
    .string()
    .max(200)
    .default('whatever legal purpose it may serve'),
  titles: LetterTitlesSchema.default(() => LetterTitlesSchema.parse({})),
  // English templates (always used as the fallback for Arabic).
  salaryCertificate: z.string().max(4000).default(DEFAULT_SALARY_CERTIFICATE_EN),
  noc: z.string().max(4000).default(DEFAULT_NOC_EN),
  experience: z.string().max(4000).default(DEFAULT_EXPERIENCE_EN),
  // Optional Arabic variants. Empty => fall back to the English template.
  salaryCertificateAr: z.string().max(4000).default(''),
  nocAr: z.string().max(4000).default(''),
  experienceAr: z.string().max(4000).default(''),
});

// ---------------------------------------------------------------------------
// Scheduled, emailed reports.
//
// Each entry describes one recurring report delivery: which report, how often,
// and to whom. Stored in the company settings JSON (no DB migration) and read
// by `scheduledReports.job.ts`, which runs once daily and fires every schedule
// that is DUE for that run (daily = every run; weekly = Mondays; monthly = the
// 1st — reckoned in the company timezone).
//
// `id` is a stable client/server-generated identifier so the UI can list and
// remove rows; the form mints one with `crypto.randomUUID()` when adding a row.
// Defaulting `reportSchedules` to `[]` keeps every pre-existing Company.settings
// JSON valid — the field materialises as an empty array.
// ---------------------------------------------------------------------------

export const ReportTypeSchema = z.enum([
  'attendance-summary',
  'timesheet',
  'document-expiry',
  'leave-balance',
]);
export type ReportType = z.infer<typeof ReportTypeSchema>;

export const ReportCadenceSchema = z.enum(['daily', 'weekly', 'monthly']);
export type ReportCadence = z.infer<typeof ReportCadenceSchema>;

export const ReportScheduleSchema = z.object({
  id: z.string().min(1).max(64),
  reportType: ReportTypeSchema,
  cadence: ReportCadenceSchema,
  recipients: z.array(z.string().email()).default([]),
  enabled: z.boolean().default(true),
});
export type ReportSchedule = z.infer<typeof ReportScheduleSchema>;

// Container schema so `reportSchedules` mirrors how letters/branding are
// modelled (a dedicated, parseable schema with a self-defaulting value). The
// PATCH /settings/:category route validates the body against this.
export const ReportSchedulesSettingsSchema = z.array(ReportScheduleSchema).default([]);
export type ReportSchedulesSettings = z.infer<typeof ReportSchedulesSettingsSchema>;

export const NotificationSettingsSchema = z.object({
  email: z
    .object({
      enabled: z.boolean().default(true),
      fromName: z.string().default('Attendance System'),
      fromEmail: z.string().email().optional().nullable(),
      smtpHost: z.string().optional().nullable(),
      smtpPort: z.number().int().min(1).max(65535).default(587),
      smtpUser: z.string().optional().nullable(),
      smtpSecure: z.boolean().default(false),
    })
    // Same defaults-from-empty defence as AttendanceSettingsSchema.sources —
    // a Company.settings JSON that doesn't include `notifications.email`
    // shouldn't 422 every settings-reading endpoint.
    .default(() => ({
      enabled: true,
      fromName: 'Attendance System',
      fromEmail: null,
      smtpHost: null,
      smtpPort: 587,
      smtpUser: null,
      smtpSecure: false,
    })),
  /**
   * Delivery master switch. Notifications are always persisted in-app; this
   * only controls whether the message is also emailed. Email is the only
   * external channel (WhatsApp/Slack were removed). Readiness (SMTP
   * credentials present) is reported via `/api/system/channels-status`.
   */
  channels: z
    .object({
      email: z.boolean().default(true),
    })
    .default(() => ({ email: true })),
  triggers: z
    .object({
      lateArrivalToManager: z.boolean().default(true),
      // Days-before-expiry thresholds. Non-negative integers only — the job
      // matches `daysRemaining` against this list exactly, so a float or a
      // negative would silently never fire. Tightened to match the client's
      // `parseDayList` (P2-18).
      documentExpiryAlertDaysBefore: z
        .array(z.number().int().nonnegative())
        .default([90, 60, 30, 7]),
    })
    .default(() => ({
      lateArrivalToManager: true,
      documentExpiryAlertDaysBefore: [90, 60, 30, 7],
    })),
});

export const WpsBankCodeSchema = z.enum([
  'GENERIC_SIF',
  'ENBD',
  'FAB',
  'MASHREQ',
  'ADCB',
  'RAKBANK',
]);
export type WpsBankCode = z.infer<typeof WpsBankCodeSchema>;

export const UaeComplianceSettingsSchema = z.object({
  ramadanModeEnabled: z.boolean().default(true),
  ramadanShiftReductionHours: z.number().min(0).max(8).default(2),
  /**
   * Operator-configured Ramadan window. The Hijri calendar varies per
   * country authority, so the system can't infer it — admin sets the
   * dates each year from the UAE official announcement. ISO date strings
   * (YYYY-MM-DD) interpreted against the company timezone. When either
   * date is empty the system treats Ramadan as not yet configured for
   * the year (no reduction applied even with ramadanModeEnabled=true).
   */
  ramadanStartDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$|^$/u, 'Expected YYYY-MM-DD').default(''),
  ramadanEndDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$|^$/u, 'Expected YYYY-MM-DD').default(''),
  /**
   * Unpaid break minutes observed during Ramadan for the flexible standard-day
   * model. Default 0: fasting crews take no lunch, so the window-overlap break
   * is disabled and the target reduction alone shortens the day. Set > 0 only if
   * a site still observes a midday break in Ramadan (the normal lunch window is
   * reused). No effect on the legacy shift-based path.
   */
  ramadanBreakMinutes: z.number().int().min(0).max(240).default(0),
  minRestBetweenShiftsHours: z.number().int().min(0).max(24).default(11),
  maxConsecutiveWorkDays: z.number().int().min(1).max(14).default(6),
  maxWeeklyHours: z.number().int().min(20).max(80).default(48),
  /**
   * Preferred bank-specific WPS (Wage Protection System) SIF format the
   * admin wants the export defaulted to. `GENERIC_SIF` follows the Central
   * Bank's reference layout; the named banks are bank-specific overlays.
   * The export page also lets admins pick a different format per-export
   * without changing this company-wide default.
   */
  wpsBankCode: WpsBankCodeSchema.default('GENERIC_SIF'),
});

// ---------------------------------------------------------------------------
// Terminology — lets a tenant relabel the core nouns the UI uses. A construction
// company tracks "Workers" across "Sites & Camps"; an office tracks "Employees"
// across "Worksites". The `profile` picks a default word set; `overrides` lets
// the admin fine-tune any single term. Resolved terms ride on the public
// branding payload so every client (even pre-auth) renders consistent wording.
//
// Two distinct people-nouns are supported because a company can have both:
// `employees`/`employee` label staff who sign in and self check-in, while
// `workers`/`worker` label field/site staff who punch on the shared tablet app.
// UI surfaces pick whichever noun matches who that screen is for.
// ---------------------------------------------------------------------------

export const TerminologyProfileSchema = z.enum(['hr', 'construction']);

export const TerminologyOverridesSchema = z
  .object({
    employees: z.string().trim().max(40).optional(),
    employee: z.string().trim().max(40).optional(),
    workers: z.string().trim().max(40).optional(),
    worker: z.string().trim().max(40).optional(),
    worksites: z.string().trim().max(40).optional(),
    worksite: z.string().trim().max(40).optional(),
    team: z.string().trim().max(40).optional(),
  })
  .default({});

export const TerminologySettingsSchema = z.object({
  profile: TerminologyProfileSchema.default('hr'),
  overrides: TerminologyOverridesSchema,
});
export type TerminologySettings = z.infer<typeof TerminologySettingsSchema>;

/** The schema must keep producing exactly the shape `resolveTerminology` reads. */
const _terminologyShapeMatches: TerminologySettingsInput = {} as TerminologySettings;
void _terminologyShapeMatches;

// ---------------------------------------------------------------------------
// Feature flags. The keys, the `FeatureFlags` type, `FEATURE_DEFAULTS` and the
// lean preset are plain data and live in `../features.js` (zod-free, so the web
// boot path can read them without loading zod); they are re-exported at the
// bottom of this file. What stays here is the validation schema.
// ---------------------------------------------------------------------------

export const FeatureFlagsSchema = z.object({
  leave: z.boolean().default(true), // leave requests, approvals, calendar, types, holidays
  corrections: z.boolean().default(true), // attendance correction requests
  announcements: z.boolean().default(true),
  letters: z.boolean().default(true), // HR letters (salary cert / NOC / experience)
  payroll: z.boolean().default(true), // payroll prep + WPS export
  reports: z.boolean().default(true), // the Reports hub
  shifts: z.boolean().default(true), // shifts + schedules
  team: z.boolean().default(true), // team directory + team attendance (managers)
  orgStructure: z.boolean().default(true), // departments + designations
  documents: z.boolean().default(true), // worker document store + expiry
  faceRecognition: z.boolean().default(true), // field face enrollment
  gratuity: z.boolean().default(true), // end-of-service gratuity calculator
  lifecycle: z.boolean().default(true), // onboarding / offboarding
  dailyRoster: z.boolean().default(true), // camp muster roster — bulk manual attendance entry for a whole camp
  selfService: z.boolean().default(true), // personal "My…" self-service screens (check-in/out, my attendance, profile). Off = management-console-only web (login-less site workers punch via the field APK/kiosk).
  workers: z.boolean().default(true), // does the company employ login-less site workers at all — the whole site-workforce pillar (field/kiosk capture, worker roster, site/camp boards & reports). Off = office-only tenant; existing worker data is soft-hidden, never deleted.
  camps: z.boolean().default(true), // does the company run labour camps at all — the whole camp pillar (occupancy/capacity boards, camp muster roster, camp<->site journey surfaces). Off = attendance-only tenant that checks in/out at SITES; existing CAMP worksites and their recorded history are soft-hidden, never deleted, and NO stored or recomputed attendance figure changes.
  hardwareTerminals: z.boolean().default(true), // standalone ZKTeco/Hikvision face terminals (ADMS/ISAPI push). Off = registering new ones is refused server-side too; already-registered terminals and the attendance they recorded are untouched.
});
/**
 * The schema and the hand-written `FeatureFlags` must stay in step. These two
 * assignments are mutual assignability checks: add a flag to the schema without
 * adding it to `FEATURE_KEYS` (or vice versa) and typecheck fails here rather
 * than at some call site later.
 */
const _flagsFromSchema: FeatureFlags = {} as z.infer<typeof FeatureFlagsSchema>;
const _flagsToSchema: z.infer<typeof FeatureFlagsSchema> = {} as FeatureFlags;
void _flagsFromSchema;
void _flagsToSchema;

// ---------------------------------------------------------------------------
// App lock — two-layer kiosk lock for the tablet/APK deployment. Layer 1: a
// short PIN to OPEN the app for everyday attendance use on a shared device.
// Layer 2: a separate ADMIN PIN that gates Settings. Only bcrypt hashes are
// stored here; the raw PINs are set via a dedicated route that hashes them, and
// the hashes are never returned to clients (status exposes booleans only). This
// is intentionally NOT a SettingsCategory, so it can't be written through the
// generic PATCH /settings/:category (which would let a raw hash be injected).
// ---------------------------------------------------------------------------
export const AppLockSettingsSchema = z.object({
  enabled: z.boolean().default(false),
  openPinHash: z.string().nullable().default(null),
  adminPinHash: z.string().nullable().default(null),
});
export type AppLockSettings = z.infer<typeof AppLockSettingsSchema>;

/** Public, hash-free view of the lock config a client may read. */
export interface AppLockStatus {
  enabled: boolean;
  hasOpenPin: boolean;
  hasAdminPin: boolean;
}

/** Admin payload to (re)configure the lock. Omitted PINs are left unchanged. */
export const AppLockConfigInputSchema = z.object({
  enabled: z.boolean(),
  openPin: z.string().regex(/^\d{4,8}$/, 'PIN must be 4–8 digits').optional(),
  adminPin: z.string().regex(/^\d{4,8}$/, 'PIN must be 4–8 digits').optional(),
});
export type AppLockConfigInput = z.infer<typeof AppLockConfigInputSchema>;

/** A PIN-verification attempt against one of the two lock layers. */
export const AppLockVerifySchema = z.object({
  scope: z.enum(['open', 'admin']),
  pin: z.string().regex(/^\d{4,8}$/),
});
export type AppLockVerify = z.infer<typeof AppLockVerifySchema>;

// "HH:MM" 24-hour wall-clock validator for the workday break windows.
//
// STRICT on purpose, and exported so nobody writes a second, looser one. The
// obvious `\d{1,2}:\d{2}` accepts "25:99", which reaches
// `combineDateAndZonedTime` and THROWS a RangeError — one hand-edited value then
// takes down every rollup for that company. `employee.schema.ts` imports this
// for the per-worker `workStartTime` override so the two ends of the same
// precedence chain (`employee.workStartTime ?? defaultWorkday.shiftStartTime`)
// cannot disagree about which strings are times.
export const HHMM_RE = /^([01]?\d|2[0-3]):[0-5]\d$/u;

// The no-schedule baseline for imported/construction workers who have no
// Schedule row. These workers don't run a fixed shift — they arrive flexibly
// (06:00 or 08:00), put in `standardDailyHours` NET hours and leave. The rollup
// (attendanceDay.service + utils/workday) credits ACTUAL on-site presence,
// auto-deducts the season's unpaid break by window overlap, and accrues OT only
// past the target + `otTailGraceMinutes`. Summer swaps the break window to the
// UAE midday sun-break; Ramadan (UaeCompliance) reduces the target and turns the
// break off. Spec: docs/workday-overtime-transport-agency-spec-2026-06-23.md.
//
// TOP-LEVEL key (read as settings.defaultWorkday). The flexible model ACTIVATES
// only when this block is physically present in the stored settings JSON;
// pre-existing installs that never wrote it keep the legacy no-schedule
// behaviour (gross worked, no OT/late).
//
// `shiftStartTime` IS the working start for a tenant with no shift. The rollup
// measures each worker's lateness day-by-day from their OWN first check-in
// against this time (resolved in the company timezone) with
// `attendance.graceDefaultMinutes` of grace, using the same comparison and the
// same strict `>` as the scheduled-shift path — so adding shifts later does not
// change what "late" means. Until 2026-08 it was read but deliberately IGNORED
// here: the model assumed arrival was flexible (a site worker turns up at 06:00
// or 08:00, puts in the net hours and leaves), so there was no start to be late
// against. That left `lateMinutes` pinned at 0 and the `LATE` status physically
// unreachable for every tenant not running the shift module — which is most of
// them — while the attendance portal the operation works from was marking those
// same arrivals late. A tenant that genuinely wants flexible arrival now says so
// by widening `attendance.graceDefaultMinutes` or by running real shifts, rather
// than by the engine silently refusing to measure.
export const DefaultWorkdaySchema = z.object({
  // NET worked hours, i.e. AFTER the unpaid break is deducted. The operation
  // runs an 8h working day inside a 9h presence (8h work + 1h break), so the
  // default is 8 — NOT the 9 the June 2026 spec assumed. A 9h default books the
  // hour between 8h and 9h net as regular time instead of overtime. Confirmed
  // against the client's live ERP upload, where a whole rest day worked posts as
  // 8 premium hours, never 9. See
  // docs/erp-clockcardsbatch-export-spec-2026-08-08.md §5.5.
  standardDailyHours: z.number().min(1).max(24).default(8),
  // The working start, in the company's timezone. Drives late detection for
  // workers with no scheduled shift (see the block comment above); it does NOT
  // move any pay figure — hours are still credited from actual presence.
  shiftStartTime: z.string().regex(HHMM_RE, 'Expected HH:MM').default('07:00'),
  // The working END, in the company's timezone. Added 2026-08-14: the flexible
  // path had a start and no end, so a crew told "07:00 to 16:00" could only be
  // expressed as "starts at 07:00", and LEAVING EARLY was not measurable at all
  // — there was nothing to say when "early" began.
  //
  // Like shiftStartTime it does NOT move a pay figure. Hours are credited from
  // actual presence and OT still accrues past standardDailyHours + the tail
  // grace; this decides only whether a day is FLAGGED as an early departure,
  // which is the exact mirror of what shiftStartTime does for lateness.
  shiftEndTime: z.string().regex(HHMM_RE, 'Expected HH:MM').default('16:00'),
  // Wash-up / down-to-the-bus tail after finishing: unpaid, NOT overtime. OT
  // accrues only beyond standardDailyHours + this. (Stay 45m → 15m OT.)
  otTailGraceMinutes: z.number().int().min(0).max(240).default(30),
  // Normal (non-summer) unpaid lunch window, auto-deducted by overlap.
  normalBreakWindow: z
    .object({
      start: z.string().regex(HHMM_RE).default('12:00'),
      end: z.string().regex(HHMM_RE).default('13:00'),
    })
    .default(() => ({ start: '12:00', end: '13:00' })),
  // Summer sun-break (UAE midday work ban). Recurring MM-DD window (the ban
  // recurs yearly on fixed Gregorian dates, unlike Ramadan), editable.
  summerBreakEnabled: z.boolean().default(true),
  summerBreakWindow: z
    .object({
      start: z.string().regex(HHMM_RE).default('12:30'),
      end: z.string().regex(HHMM_RE).default('15:00'),
    })
    .default(() => ({ start: '12:30', end: '15:00' })),
  summerBreakStartDate: z
    .string()
    .regex(/^\d{2}-\d{2}$|^$/u, 'Expected MM-DD')
    .default('06-15'),
  summerBreakEndDate: z
    .string()
    .regex(/^\d{2}-\d{2}$|^$/u, 'Expected MM-DD')
    .default('09-15'),
});
export type DefaultWorkday = z.infer<typeof DefaultWorkdaySchema>;

/**
 * Derived, read-only answer to "is the flexible net-hours + OT engine actually
 * running?" — served by `GET /api/settings/workday-status`.
 *
 * Why this exists as its own thing rather than a field on the settings payload:
 * the settings payload is `CompanySettingsSchema.parse(raw)`, and the schema
 * above carries `.default(8)` / `.default(30)`. So a company whose stored JSON
 * has NO `defaultWorkday` block still reports a configured 8-hour day with a
 * 30-minute grace, and the Settings screen renders those numbers as policy —
 * while the rollup (which branches on the RAW JSON) is deducting no break and
 * accruing zero weekday OT for every no-schedule worker. The parsed payload
 * literally cannot express "not configured"; this status can.
 *
 * `reported` vs `effective` is the whole point: when `active` is false,
 * `reported` is what the settings API/screen shows and `effective` is null,
 * because nothing is being computed from those numbers.
 *
 * This is NOT a SettingsCategory and is never writable. Activating the engine
 * changes computed hours for days that already rolled up, so it must be a
 * deliberate admin action (save the Default Workday category, or run
 * `scripts/backfill-default-settings.ts`) — never a side effect of a read.
 */
export interface WorkdayEngineStatus {
  /** True when no-schedule workers roll up on the flexible net-hours + OT path. */
  active: boolean;
  /**
   * - `active` — the engine is running.
   * - `not-configured` — no `defaultWorkday` block is stored (the common legacy
   *   case; the backfill script fixes it).
   * - `malformed` — a `defaultWorkday` key IS stored but is not an object
   *   (e.g. a string or null), which the rollup treats exactly like absent.
   *   Note this blob also makes `CompanySettingsSchema.parse` throw, so
   *   `GET /settings/all` 500s for such a company while the rollup keeps
   *   quietly computing zero overtime. This status is built defensively and
   *   still answers.
   */
  reason: 'active' | 'not-configured' | 'malformed';
  /** What `GET /settings/all` and `GET /settings/defaultWorkday` report — schema
   * defaults when nothing is stored, which is precisely the misleading part. */
  reported: { standardDailyHours: number; otTailGraceMinutes: number };
  /** What the rollup will actually apply. `null` when the engine is inactive —
   * i.e. no target and no OT threshold are in force at all. */
  effective: { standardDailyHours: number; otTailGraceMinutes: number } | null;
}

export const CompanySettingsSchema = z.object({
  general: GeneralSettingsSchema.default(() => GeneralSettingsSchema.parse({})),
  attendance: AttendanceSettingsSchema.default(() => AttendanceSettingsSchema.parse({})),
  defaultWorkday: DefaultWorkdaySchema.default(() => DefaultWorkdaySchema.parse({})),
  faceRecognition: FaceRecognitionSettingsSchema.default(() =>
    FaceRecognitionSettingsSchema.parse({})
  ),
  overtime: OvertimeSettingsSchema.default(() => OvertimeSettingsSchema.parse({})),
  leave: LeaveSettingsSchema.default(() => LeaveSettingsSchema.parse({})),
  security: SecuritySettingsSchema.default(() => SecuritySettingsSchema.parse({})),
  branding: BrandingSettingsSchema.default(() => BrandingSettingsSchema.parse({})),
  notifications: NotificationSettingsSchema.default(() =>
    NotificationSettingsSchema.parse({})
  ),
  uaeCompliance: UaeComplianceSettingsSchema.default(() =>
    UaeComplianceSettingsSchema.parse({})
  ),
  letters: LettersSettingsSchema.default(() => LettersSettingsSchema.parse({})),
  // Defaults to [] so a Company.settings JSON predating this field stays valid.
  reportSchedules: ReportSchedulesSettingsSchema,
  terminology: TerminologySettingsSchema.default(() =>
    TerminologySettingsSchema.parse({})
  ),
  features: FeatureFlagsSchema.default(() => FeatureFlagsSchema.parse({})),
  appLock: AppLockSettingsSchema.default(() => AppLockSettingsSchema.parse({})),
  // The ERP file exchange (docs/erp-clockcardsbatch-export-spec-2026-08-08.md).
  // Lives in its own module: this file is over its size budget and may only
  // shrink.
  erp: ErpSettingsSchema.default(() => ErpSettingsSchema.parse({})),
});
export type CompanySettings = z.infer<typeof CompanySettingsSchema>;

export const SettingsCategorySchema = z.enum([
  'general',
  'attendance',
  'defaultWorkday',
  'faceRecognition',
  'overtime',
  'leave',
  'security',
  'branding',
  'notifications',
  'uaeCompliance',
  'letters',
  'reportSchedules',
  'terminology',
  'features',
  'erp',
]);
export type SettingsCategory = z.infer<typeof SettingsCategorySchema>;

export const CompanyInfoSchema = z.object({
  name: z.string().min(1).max(120),
  legalName: z.string().max(180).optional().nullable(),
  emiratesId: z.string().max(40).optional().nullable(),
  trnNumber: z.string().max(40).optional().nullable(),
  address: z.string().max(500).optional().nullable(),
  phone: z.string().max(40).optional().nullable(),
  email: z.string().email().optional().nullable().or(z.literal('')),
  website: z.string().max(200).optional().nullable(),
  timezone: z.string().default('Asia/Dubai'),
  currency: z.string().default('AED'),
  weekendDays: z.array(z.number().min(0).max(6)).default([5, 6]),
  primaryColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/).default('#6C47FF'),
});
export type CompanyInfo = z.infer<typeof CompanyInfoSchema>;
